import { HStack, Text, VStack } from "@chakra-ui/react"
import ItemSelector, {
  ItemSelectorProps as PrimitiveItemSelectorProps
} from "@pathwright/ux/src/components/item-selector/ItemSelector"
import { ItemInputImage } from "@pathwright/ux/src/components/item-selector/ItemInput"
import Pathicon from "../../pathicon/Pathicon"
import { GroupAvatars } from "../../unified-home/components/GroupAvatars"
import RoleBadge, {
  getRoleOptions,
  serializeRoleBadgesToRegistrationRoles
} from "./RoleBadge"
import { SelectedCohort, SelectedResource } from "./state"

type PathObject = {
  id: number
  name: string
  image?: string | null
  role?: SelectedCohort["role"] | SelectedResource["role"]
  sourceCohort?: SelectedResource["sourceCohort"]
  registrations?: SelectedCohort["registrations"]
}

export type PathObjectSelectorProps<T extends PathObject> = Omit<
  PrimitiveItemSelectorProps<T>,
  "renderItemContent" | "getItemImage"
> & {
  kind: "resource" | "cohort"
}

function PathObjectSelector<T extends PathObject>({
  kind,
  icon,
  ...props
}: PathObjectSelectorProps<T>) {
  function getItemRoleOptions(
    option: T,
    roleKey: "resourceRole" | "cohortRole"
  ) {
    return getRoleOptions().filter((roleOption) => {
      const roles = serializeRoleBadgesToRegistrationRoles([roleOption])
      return option.role === roles[roleKey]
    })
  }

  return (
    <ItemSelector
      {...props}
      icon={icon}
      getItemImage={
        kind === "resource" ? (option) => option.image ?? null : undefined
      }
      renderItemContent={(option, context) => {
        if (kind === "cohort") {
          return context === "menu" ? (
            <VStack gap={0} w="100%">
              <HStack w="100%">
                <GroupAvatars
                  group={option}
                  showGroupName
                  fontColor="blackAlpha.900"
                  lineClamp={1}
                />
                {!!option.role && (
                  <HStack flexGrow={1} justifyContent="flex-end">
                    {getItemRoleOptions(option, "cohortRole").map((roleOption) => (
                      <RoleBadge
                        key={roleOption.value}
                        badge={roleOption.badge}
                        iconOnly
                      />
                    ))}
                  </HStack>
                )}
              </HStack>
            </VStack>
          ) : (
            <GroupAvatars group={option} showGroupName />
          )
        }

        return context === "menu" ? (
          <VStack gap={0} w="100%">
            <HStack w="100%" justifyContent="flex-start">
              {option.image ? (
                <ItemInputImage image={option.image} />
              ) : icon ? (
                <Pathicon icon={icon} />
              ) : null}
              <Text as="span" mr="auto">
                {option.name}
              </Text>
              {!!option.role && (
                <HStack flexGrow={1} justifyContent="flex-end">
                  {getItemRoleOptions(option, "resourceRole").map(
                    (roleOption) => (
                      <RoleBadge
                        key={roleOption.value}
                        badge={roleOption.badge}
                        iconOnly
                      />
                    )
                  )}
                </HStack>
              )}
            </HStack>
          </VStack>
        ) : (
          <HStack>
            {option.image ? (
              <ItemInputImage image={option.image} />
            ) : icon ? (
              <Pathicon icon={icon} />
            ) : null}
            <Text as="span" color="whiteAlpha.900">
              {option.name}
            </Text>
          </HStack>
        )
      }}
    />
  )
}

export default PathObjectSelector
